import { createContext, useContext, useEffect, useState } from "react";
import useSignalR from "hooks/useSignalR";

const UploadContext = createContext();

export const UploadingStateType = Object.freeze({
	None: "None",
	Uploading: "Uploading",
	Validating: "Validating",
	Processing: "Processing"
});

export const UploadInProgressProvider = ({ children }) => {
	const [uploadingState, setUploadingState] = useState(UploadingStateType.None);
	const [processComplete, setProcessComplete] = useState(null);
	const [searchTagsReady, setSearchTagsReady] = useState(null);
	const { connection, registerHandler, onConnected } = useSignalR();

	const value = {
		uploadingState,
		setUploadingState,
		processComplete,
		setProcessComplete,
		searchTagsReady,
		setSearchTagsReady
	};

	/**
	 * This useEffect controls the SignalR connection and message registration; putting this in another file/component/hook seems to break the 
	 * system - multiple useSignalR instance lose the "handlers" ref link between the 2 useEffects in that hook.
	 * It does mean until this is fixed, there is no re-usable component/hook and this useEffect will more or less be duplicated whenever
	 * SignalR is to be used.
	 */
	useEffect(() => {
		if (!connection) {
			return;
		}

		onConnected(async (conn) => {
			console.debug("Connection ready - attempting to join group");
			await conn.invoke("JoinGroupAsync", "CourseEditorContent");
		});

		// Handle reconnections
		connection.onreconnected(async () => {
			console.debug("Reconnected - rejoining group");
			await connection.invoke("JoinGroupAsync", "CourseEditorContent");
		});

		const unregisterUnzip = registerHandler("UnzipCourse", "CourseEditorContent", (message) => {
			console.debug("Processing message", message);
			setProcessComplete(message);
		});

		// Batch transcription finishes minutes after UnzipCourse; the message is retained until a submodule consumes it, so arrival order does not matter.
		const unregisterSearchTags = registerHandler("SearchTagsReady", "CourseEditorContent", (message) => {
			console.debug("Search tags ready message", message);
			setSearchTagsReady(message);
		});

		return () => {
			unregisterUnzip();
			unregisterSearchTags();
		};
	}, [connection, onConnected, registerHandler]);

	return (
		<UploadContext.Provider value={value}>
			{children}
		</UploadContext.Provider>
	);
};

export const useUploadInProgress = () => useContext(UploadContext);